/**
 * 轻量 SEO / head 管理器（零依赖）。
 *
 * 为什么不用 vue-meta：本项目是 vue-cli 2 + webpack 3 的老模板，引入新依赖要动的
 * 构建链较多，而这里需要的能力很有限——按路由写 title / description / canonical /
 * OG / Twitter / JSON-LD。于是用原生 DOM API 直接维护 <head>：
 *   · 同一份文案来自 src/seo/routes.js，浏览器端与预渲染/站点地图完全一致
 *   · 标签按 data-seo 标记复用，路由来回切换不会堆叠重复 meta
 */

import seoRoutes from '@/seo/routes'
import site from '@/seo/site'

/** 需要按路由动态维护的 meta 标签：[属性名, 属性值, 内容字段] */
const META_TAGS = [
  ['name', 'description', 'description'],
  ['name', 'keywords', 'keywords'],
  ['name', 'robots', 'robots'],
  ['name', 'author', null],
  ['property', 'og:title', 'title'],
  ['property', 'og:description', 'description'],
  ['property', 'og:url', 'canonical'],
  ['property', 'og:type', 'ogType'],
  ['property', 'og:site_name', null],
  ['property', 'og:locale', null],
  ['property', 'og:image', 'ogImage'],
  ['name', 'twitter:card', null],
  ['name', 'twitter:title', 'title'],
  ['name', 'twitter:description', 'description'],
  ['name', 'twitter:image', 'ogImage']
]

/** 站内固定内容：与 src/seo/site.js 保持一致，避免多处硬编码 */
const STATIC_META = {
  author: site.legalName,
  'og:site_name': site.name,
  'og:locale': site.locale,
  'twitter:card': 'summary_large_image'
}

function absolute(pathOrUrl) {
  const value = String(pathOrUrl || '')
  // 站点部署在子路径时，站内资源地址同样要带访问前缀
  const base = site.basePath || '/'
  if (!value) return site.url + base
  if (/^https?:\/\//i.test(value)) return value
  return site.url + base + (value.charAt(0) === '/' ? value.slice(1) : value)
}

/** 查找或创建 meta 标签（用 data-seo 标记，便于回滚与避免重复） */
function upsertMeta(attr, key, content) {
  if (!content) return
  let el = document.head.querySelector(`meta[data-seo="${attr}:${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    el.setAttribute('data-seo', `${attr}:${key}`)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** 查找或创建 link 标签（canonical 等） */
function upsertLink(rel, href) {
  if (!href) return
  let el = document.head.querySelector(`link[data-seo="link:${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    el.setAttribute('data-seo', `link:${rel}`)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/** 写入 / 覆盖一段 JSON-LD 结构化数据 */
function upsertJsonLd(id, data) {
  const selector = `script[type="application/ld+json"][data-seo="${id}"]`
  let el = document.head.querySelector(selector)
  if (!el) {
    el = document.createElement('script')
    el.setAttribute('type', 'application/ld+json')
    el.setAttribute('data-seo', id)
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

/** 公司主体结构化数据（全站通用，首页额外通过 WebSite 描述站内搜索） */
function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': site.url + '/#organization',
    name: site.legalName,
    alternateName: site.name,
    url: site.url + '/',
    email: site.email,
    logo: absolute(site.logo),
    description: site.description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: site.address.street,
      addressLocality: site.address.locality,
      addressRegion: site.address.region,
      addressCountry: site.address.country
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: '商务合作',
      email: site.email,
      availableLanguage: ['zh-CN']
    }
  }
}

function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': site.url + '/#website',
    name: site.name,
    alternateName: site.legalName,
    url: site.url + '/',
    inLanguage: 'zh-CN',
    publisher: { '@id': site.url + '/#organization' },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: site.url + site.searchPath
      },
      'query-input': 'required name=search_term_string'
    }
  }
}

/** 面包屑中间层级（crumbs 里的父级名称）对应的真实路径 */
const CRUMB_HREF = {
  数据产品: '/api',
  接口列表: '/allApi',
  关于我们: '/aboutUs/case1'
}

/** 面包屑：首页 → … → 当前页（依据 routes.js 的 crumbs） */
function breadcrumbJsonLd(config) {
  const crumbs = config.crumbs || []
  if (!crumbs.length || config.path === '/') return null

  const items = [{ name: '首页', url: site.url + '/' }]
  crumbs.forEach((name, index) => {
    const isLast = index === crumbs.length - 1
    const href = isLast ? config.canonical : site.url + (CRUMB_HREF[name] || '/')
    items.push({ name: name, url: href })
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  }
}

/** 页面主体结构化数据（描述"这一页是什么"） */
function pageJsonLd(config) {
  const graph = {
    '@context': 'https://schema.org',
    '@type': config.type || 'WebPage',
    '@id': config.canonical + '#webpage',
    url: config.canonical,
    name: config.title,
    description: config.description,
    inLanguage: 'zh-CN',
    isPartOf: { '@id': site.url + '/#website' },
    publisher: { '@id': site.url + '/#organization' }
  }
  if (config.path === '/') {
    graph.primaryImageOfPage = { '@type': 'ImageObject', url: absolute(site.ogImage) }
  }
  return graph
}

/**
 * 生成某个路由的完整 head 数据（预渲染脚本也复用该函数，保证静态产物与运行时一致）
 * @param {string} pathname 例如 /api
 * @param {string} [ogType] 覆盖 og:type（默认 website）
 */
export function buildHead(pathname, ogType) {
  const config = seoRoutes.resolve(pathname)
  return {
    path: config.path,
    // crumbs / type 供结构化数据使用（buildJsonLd 直接消费该对象）
    crumbs: config.crumbs,
    type: config.type,
    title: config.title,
    description: config.description,
    keywords: config.keywords,
    robots: config.robots || seoRoutes.defaultRobots,
    canonical: seoRoutes.canonical(config.path || pathname),
    ogType: ogType || 'website',
    ogImage: absolute(site.ogImage)
  }
}

/** 生成结构化数据列表（Organization / WebSite 全站都有） */
export function buildJsonLd(pathname) {
  const config = buildHead(pathname)
  const graphs = [organizationJsonLd()]

  if (pathname === '/' || seoRoutes.normalize(pathname) === '/') graphs.push(websiteJsonLd())

  graphs.push(pageJsonLd(config))

  const crumbs = breadcrumbJsonLd(config)
  if (crumbs) graphs.push(crumbs)

  return graphs
}

/** 把 head 数据写入当前文档 */
export function applyHead(pathname) {
  if (typeof document === 'undefined') return null
  const head = buildHead(pathname)

  document.title = head.title

  META_TAGS.forEach(([attr, key, field]) => {
    const content = field ? head[field] : STATIC_META[key]
    upsertMeta(attr, key, content)
  })

  upsertLink('canonical', head.canonical)

  // 结构化数据条数随页面类型变化，先清空旧的再写入，避免路由切换后残留
  Array.prototype.slice
    .call(document.head.querySelectorAll('script[type="application/ld+json"][data-seo]'))
    .forEach(el => el.parentNode.removeChild(el))

  buildJsonLd(pathname).forEach((graph, index) => {
    upsertJsonLd(`jsonld-${index}`, graph)
  })

  if (typeof document.documentElement.setAttribute === 'function') {
    document.documentElement.setAttribute('lang', 'zh-CN')
  }
  // 预渲染脚本据此判断"head 已由应用写就绪"
  document.documentElement.setAttribute('data-seo-applied', head.path)

  return head
}

/**
 * 挂到 router 上：首次进入 + 每次路由切换都刷新 head。
 * 返回卸载函数（一般不需要，保留给测试用）。
 */
export function installSeo(router) {
  const run = to => {
    try {
      applyHead(to.path)
    } catch (e) {
      // SEO 失败不应影响页面功能
      if (process.env.NODE_ENV !== 'production') console.warn('[seo] applyHead failed', e)
    }
  }

  router.afterEach(run)

  if (router.currentRoute) run(router.currentRoute)
}

export default { buildHead, buildJsonLd, applyHead, installSeo }



// WEBPACK FOOTER //
// ./src/utils/seo.js